/**
 * @ngdoc service
 * @name mx.SolutionBuilderAgent.DialogOpenerService
 * @module mx.SolutionBuilderAgent
 *
 * @description
 * A service provides a way for opening most common dialogs like:
 * `ObjectDetails`, `NewDialog`, `EditDialog`, `ObjectsList`, `selectObjects`, `selectFiles`.
 */
DialogOpenerService.$inject = [
	'$q',
	'$mdDialog',
	'mx.shell.DetailsView',
	'mx.internationalization',
	'mx.shell.SidePanelService',
	'mx.shell.SideLayout',
	'mx.shell.RoutingService',
	'mx.shell.EventService',
	'mx.shell.DetailsView',
	'mx.SolutionBuilderAgent.ContentWidgetService',
	'mx.SolutionBuilderAgent.WizardService',
	'mx.SolutionBuilderAgent.WidgetManager',
	'mx.SolutionBuilderAgent.RecentService',
	'mx.SolutionBuilderAgent.MultiSelectionTabService',
	'mx.sb.DialogView.DialogFactory',
	'mx.SolutionBuilderAgent.EntityService',
	'mx.SolutionBuilderAgent.PersonalizationService',
];

export function DialogOpenerService(
	$q,
	$mdDialog,
	MxDetailsView,
	i18n,
	sidePanelService,
	sideLayoutService,
	routingService,
	eventService,
	DetailsView,
	contentWidgetService,
	wizardService,
	widgetManager,
	recentService,
	multiSelectionTabService,
	dialogFactory,
	entityService,
	personalizationService
) {
	eventService.subscribeGlobal(mx.SolutionBuilderAgent.Events.ForceOpenObjectDetails, function (e, params) {
		openPreview(params, undefined, undefined, params.parentView);
	});

	eventService.subscribeGlobal(mx.SolutionBuilderAgent.Events.ForceOpenObjectsGrid, function (e, params) {
		openObjectsListPreview(
			{
				entityClassName: params.entityClassName,
				label: params.displayName,
				selectedItemIds: params.ids,
			},
			params.parentView
		).catch(mx.logCatch);
	});

	return {
		openPreview: openPreview,
		openNewDialog: openNewDialog,
		openEditDialog: openEditDialog,
		openObjectsListPreview: openObjectsListPreview,
		openLocalizationDialog: openLocalizationDialog,
		selectObjects: selectObjects,
		selectFiles: selectFiles,
		selectHierarchyObject: selectHierarchyObject,
		selectImage: selectImage,
	};

	/**
	 * @ngdoc method
	 * @name mx.SolutionBuilderAgent.DialogOpenerService#openLocalizationDialog
	 *
	 * @description
	 * Opens a Dialog used to localize localizable values
	 *
	 * @param {object} params - localization params:
	 *                {mx.SolutionBuilderAgent.LocalizationProperty} localizedValue - {@ref mx.SolutionBuilderAgent.LocalizationProperty localization property} with translations filled
	 *                {boolean} saveLocalizationDirectly - show Save button and save localization directly to server
	 *                {string} title - dialog's title
	 *                {boolean} richTextMode - rich text localization is intended
	 * @return {promise} A promise, resolved to a {@ref mx.SolutionBuilderAgent.LocalizationProperty localization property}
	 */
	function openLocalizationDialog(params) {
		const dialogContentTemplate =
			'<md-dialog aria-label="Designer Dialog" class="mx-localization-editor__dialog">' +
			'  <md-dialog-content class="mx-localization-editor__dialog--content">' +
			'    <mx-localization-editor ' +
			'      flex ' +
			'      data-localized-value="vm.localizedValue" ' +
			'      data-supported-languages="vm.supportedLanguages" ' +
			'      data-result="vm.result" ' +
			'      data-dialog="dialog" ' +
			'      save-localization-directly="{{vm.saveLocalizationDirectly}}" ' +
			'      title="{{:: vm.title}}" ' +
			'      active-mode="{{vm.activeMode}}" ' +
			'      rich-text-mode-options="vm.richTextModeOptions" ' +
			'      rich-text-mode=":: vm.richTextMode" ' +
			'      custom-editor=":: vm.customEditor">' +
			'     </mx-localization-editor>' +
			'  </md-dialog-content>' +
			'</md-dialog>';

		const result = $q.defer();

		params.result = result;

		const dialogParams = {
			template: dialogContentTemplate,
			locals: { params: params },
			controller: DialogController,
		};

		$mdDialog.show(dialogParams);

		DialogController.$inject = ['$scope', '$mdDialog', 'params'];

		function DialogController($scope, $mdDialog, params) {
			$scope.vm = params;
			$scope.dialog = $mdDialog;
		}

		return result.promise;
	}

	function preparePresetsForWizard(presetFields, entityType) {
		if (typeof presetFields === 'object') {
			const presets = {};
			presets.Context = presetFields.Context;
			delete presetFields.Context;
			presets[entityType] = presetFields;
			return presets;
		}
	}

	/**
	 * @ngdoc method
	 * @name mx.SolutionBuilderAgent.DialogOpenerService#openNewDialog
	 *
	 * @description
	 * Opens a `Create New Dialog` by given objectType.
	 *
	 * @param {string} objectType - object type.
	 * @param {object} action - <i>[optional]</i> action.
	 * @param {object[]} actionContext - <i>[optional]</i> selected objects from action.
	 * @param {boolean} openInFullScreen - <i>[optional]</i>flag, that indicates if dialog should be opened in full screen.
	 * @param {object} presetFields - <i>[optional]</i> some preset params if exists.
	 * @param {DetailsView} parentView - <i>[optional]</i> if set - this opened New Dialog will be closed automatically if parent view is closed.
	 * @param {boolean} showDetailsAfterCreation - <i>[optional]</i> <i>[default: 'true']</i> if `true` then just created object details will be shown.
	 * @param {object} outerContext - <i>[optional]</i>Object with fields to be added to a new view's Context.OuterContext.
	 * @param {boolean} related - <i>[optional]</i> true if new view is related to parentView and blocks parentView
	 * @param {string} widgetId widget id
	 * @param {string|undefined} telemetrySource source field for telemetry on open event
	 * @returns {{newEntityCreatedPromise: promise,	newDialogOpenedPromise: promise}} - promise, which will be successfully resolved when new item is created.
	 */
	function openNewDialog(
		objectType,
		action,
		actionContext,
		openInFullScreen,
		presetFields,
		parentView,
		showDetailsAfterCreation,
		outerContext,
		related,
		widgetId,
		telemetrySource
	) {
		// TODO: Is it possible to move out opening Details View out from this method?
		// It should be done on the same level as calling openNewDialog().
		// Something like mxDialogOpener.openNewDialog(...).newEntityCreatedPromise.then(mxDialogOpener.openPreview(...));
		showDetailsAfterCreation = showDetailsAfterCreation === undefined ? true : Boolean(showDetailsAfterCreation);

		let view;
		const newEntityCreatedDefer = $q.defer();
		const newDialogOpenedDefer = $q.defer();
		let alwaysFullScreen = true;
		if (objectType) {
			__getNewDialogView(objectType, action, widgetId).then(
				function (addView) {
					if (addView) {
						const commonNewName = i18n.get('solutionBuilderAgent.common.new');
						const title = `${addView.EntityDisplayName}: ${commonNewName}`;
						const viewId = `solutionBuilderAgent.common.new_${objectType}${Math.random()}`;

						if (addView.ShowAsWizard === true || !recentService.enabled) {
							alwaysFullScreen = !addView.ShowAsWizard;

							view = wizardService.getWizardView({
								typeName: addView.Entity,
								widgetId: addView.Id,
								actionId: action ? action.id : addView.Id,
								actionName: action ? action.name : '',
								wizardMode: mx.SolutionBuilderAgent.WizardMode.New,
								name: commonNewName,
								displayName: commonNewName,
								finishTitle: '',
								customHeader: title,
								selectedObjects: actionContext,
								presetFields: preparePresetsForWizard(presetFields, addView.Entity),
								outerContext: outerContext,
							});
							view.id = viewId;
							view.alwaysFullScreen = alwaysFullScreen;
							view.headerIcon = addView.EntityIcon;
							view.headerColor = addView.EntityColor;
							view.headerTitle = addView.EntityDisplayName;
							view.ShowAsWizard = true;
							view.dialogType = mx.SolutionBuilderAgent.ContentWidgetDialogAction.New;

							openInFullScreen = false;
						} else {
							const context = {
								_type: objectType,
								_name: commonNewName,
								_displayName: commonNewName,
							};
							view = dialogFactory.createNewDialog(context, addView, {
								id: viewId,
								outerContext: outerContext,
								presetFields: presetFields,
								controllerParams: action ? action.controllerParams : '',
								selectedObjects: actionContext,
							});
						}

						if (action) {
							view.startedFromAction = action;
							view.actionContext = actionContext;
						}

						// Set URL for View, by which this view can be opened in separate browser tab.
						const objectContext = {
							_type: objectType,
						};
						contentWidgetService.setViewUrl(view, addView.Id, objectContext);

						view.locals.view = view;

						view.telemetrySource = telemetrySource;

						sidePanelService
							.addView(view, parentView, related)
							.then(function (res) {
								newEntityCreatedDefer.resolve(res);
							})
							.catch(function (e) {
								newEntityCreatedDefer.reject(e);
							});

						if (typeof openInFullScreen !== 'undefined' && !alwaysFullScreen) {
							sideLayoutService.switchFullScreenMode(openInFullScreen);
						}
						newDialogOpenedDefer.resolve(view);
					} else {
						newEntityCreatedDefer.reject('view was not found');
						newDialogOpenedDefer.reject('view was not found');
						routingService.go('401');
					}
				},
				function () {
					newEntityCreatedDefer.reject('view was not found');
					newDialogOpenedDefer.reject('view was not found');
					console.error('A promise was rejected');
				}
			);
		} else {
			const errMessage = i18n.get('solutionBuilderAgent.errors.noTypesMsg');
			newDialogOpenedDefer.reject(errMessage);
			newEntityCreatedDefer.reject(errMessage);
			throw new Error(errMessage);
		}

		newEntityCreatedDefer.promise.then(
			function (res) {
				if (res) {
					if (showDetailsAfterCreation) {
						openPreview(res);
					} else {
						widgetManager.getPreviewView(res).then(function (previewView) {
							if (previewView) {
								recentService.addToRecent(previewView);
							}
						});
					}
				}
				return res;
			},
			function (e) {
				mx.logCatch(e);
			}
		);

		return {
			newEntityCreatedPromise: newEntityCreatedDefer.promise,
			newDialogOpenedPromise: newDialogOpenedDefer.promise,
		};
	}

	function __getNewDialogView(objectType, action, widgetId) {
		return widgetManager.getNewDialogs(objectType, widgetId || (action && action.Widget && action.Widget.Id)).then(
			async function (views) {
				views = views || [];

				if (!widgetId) {
					const personalizedWidgetId = await personalizationService.getDefaultNewDialogId(objectType);
					const view = personalizedWidgetId && views.find((v) => v.Id === personalizedWidgetId);
					if (view) {
						return view;
					}
				}

				if (action && action.Widget && action.Widget.Type === mx.SolutionBuilderAgent.ContentWidgetType.Dialog) {
					return _.find(views, { Id: action.Widget.Id });
				}

				if (widgetId) {
					const viewById = views.find((view) => view.Id === widgetId);

					if (viewById) {
						return viewById;
					}
				}

				// In case the 'Add' action is specified the views with the property of NotUseInCreateEditPurposes set to True will be ignored.
				// Otherwise, when action is null, all views are processing (for e.g. quick links).
				return (
					!action
						? views
						: views.filter(function (view) {
								return !view.NotUseInCreateEditPurposes;
						  })
				)[0];
			},
			function (e) {
				mx.logCatch(e);
			}
		);
	}

	/**
	 * @ngdoc method
	 * @name mx.SolutionBuilderAgent.DialogOpenerService#openEditDialog
	 *
	 * @description
	 * Opens a `Edit Dialog` by given objectType and ID.
	 *
	 * @param {object} objectContext - object context.
	 * @param {boolean} openInFullScreen - <i>[optional]</i> flag, that indicates if dialog should be opened in full screen.
	 * @param {object} presetFields - <i>[optional]</i> some preset params if exists.
	 * @param {string} parentView - <i>[optional]</i> if set - this opened New Dialog will be closed automatically if parent view is closed.
	 * @param {Object} extraLocals = {}- <i>[optional]</i> if set - mxView locals will be extended with passed one.
	 *
	 * @returns {{finishDialogInteractionPromise: promise, editDialogOpenedPromise: promise}}
	 */
	function openEditDialog(objectContext, openInFullScreen, presetFields, parentView, extraLocals = {}) {
		let view;
		const finishDialogInteractionDefer = $q.defer();
		const editDialogOpenedDefer = $q.defer();
		const { widgetId } = extraLocals;

		if (objectContext._type) {
			entityService
				.processObjectContext(objectContext)
				.then(() => __getEditDialogView(objectContext._type, widgetId))
				.then(
					function (editView) {
						if (!editView) {
							editDialogOpenedDefer.reject('view was not found');
							finishDialogInteractionDefer.reject('view was not found');
							routingService.go('401');
						}
						let alwaysFullScreen = true;

						if (editView.ShowAsWizard === true || !recentService.enabled) {
							alwaysFullScreen = !editView.ShowAsWizard;

							view = wizardService.getWizardView({
								typeName: editView.Entity,
								widgetId: editView.Id,
								actionId: editView.Id,
								wizardMode: mx.SolutionBuilderAgent.WizardMode.Edit,
								name: objectContext._name,
								displayName: objectContext._displayName,
								finishTitle: '',
								selectedObjects: [objectContext], //???
								presetFields: preparePresetsForWizard(presetFields, editView.Entity),
								// outerContext: outerContext,

								mergeDisabled: editView.MergeDisabled,
							});
							view.alwaysFullScreen = alwaysFullScreen;
							view.headerIcon = editView.EntityIcon;
							view.headerColor = editView.EntityColor;
							view.headerTitle = editView.EntityDisplayName;
							view.ShowAsWizard = true;
							view.dialogType = mx.SolutionBuilderAgent.ContentWidgetDialogAction.New;

							openInFullScreen = false;
						} else {
							view = dialogFactory.createEditDialog(objectContext, editView, {
								hideActions: Boolean(extraLocals.hideActions),
								isSingleView: Boolean(extraLocals.isSingleView),
								readOnly: Boolean(extraLocals.readOnly),
								presetFields: presetFields,
							});
						}

						view.locals.view = view;
						contentWidgetService.setViewUrl(view, editView.Id, objectContext);
						sidePanelService
							.addView(view, parentView)
							.then(function (res) {
								finishDialogInteractionDefer.resolve(res);
							})
							.catch(function (e) {
								finishDialogInteractionDefer.reject(e);
							});

						if (typeof openInFullScreen !== 'undefined' && !alwaysFullScreen) {
							sideLayoutService.switchFullScreenMode(openInFullScreen);
						}
						editDialogOpenedDefer.resolve(view);
					},
					function () {
						editDialogOpenedDefer.reject('view was not found');
						finishDialogInteractionDefer.reject('view was not found');
						console.error('view was not found');
					}
				);
		} else {
			const errorMessage = i18n.get('solutionBuilderAgent.new.noTypesMsg');
			editDialogOpenedDefer.reject(errorMessage);
			finishDialogInteractionDefer.reject(errorMessage);
			throw new Error(errorMessage);
		}
		return {
			finishDialogInteractionPromise: finishDialogInteractionDefer.promise,
			editDialogOpenedPromise: editDialogOpenedDefer.promise,
		};
	}

	function __getEditDialogView(objectType, viewId) {
		return widgetManager.getEditDialogs(objectType, viewId).then(
			async function (views) {
				if (!views) {
					return;
				}

				if (viewId) {
					const byId = views.filter(function (view) {
						return view.Id === viewId;
					});

					if (byId.length) {
						return byId[0];
					}
				} else {
					const personalizedWidgetId = await personalizationService.getDefaultEditDialogId(objectType);
					const view = personalizedWidgetId && views.find((v) => v.Id === personalizedWidgetId);
					if (view) {
						return view;
					}
				}

				return views[0];
			},
			function () {
				console.error('A promise was rejected');
			}
		);
	}

	/**
	 * @ngdoc method
	 * @name mx.SolutionBuilderAgent.DialogOpenerService#openPreview
	 *
	 * @description
	 * Opens a `Preview Dialog` by given objectType.
	 *
	 * @param {object} objectDetails - object details in format like this:
	 *        `parentViewId` has greater priority.
	 * ```
	 *{
	 *    _id: "f8e4e825-5501-e611-8682-50e549e83fa6",
	 *    _type: "SPSUserType",
	 *    _displayName: "Nick, Muller"
	 *}
	 *    or
	 *{
	 *    Id: "f8e4e825-5501-e611-8682-50e549e83fa6",
	 *    EntityType (or 'Sys-Entity'): "SPSUserType",
	 *    DisplayString: "Nick, Muller"
	 *}
	 * (TODO: figure out and use single unified format)
	 * ```
	 * @param {Object} [extraLocals = {}] - locals for the view controller
	 * @param {boolean} [openInFullScreen = false] - <i>[optional]</i> flag, that indicates if dialog should be opened in full screen.
	 * @param {DetailsView?} [parentView] - parent view for the new opening view
	 * @param {Object} [presetFields = {}] - object with parameters which will be used as default values for data model in the view
	 *
	 */
	async function openPreview(objectDetails, extraLocals = {}, openInFullScreen = false, parentView, presetFields = {}) {
		const { widgetId } = extraLocals;
		try {
			const detailsView = await widgetManager.getPreviewView(objectDetails, widgetId, extraLocals, presetFields);
			if (detailsView) {
				sidePanelService.addView(detailsView, parentView).catch(mx.logCatch);
				if (openInFullScreen) {
					sideLayoutService.switchFullScreenMode(true);
				}
			}

			return detailsView;
		} catch (e) {
			mx.logCatch(e);
		}
	}

	/**
	 * @ngdoc method
	 * @name mx.SolutionBuilderAgent.DialogOpenerService#selectObjects
	 *
	 * @description
	 * Opens a `Select Objects` dialog.
	 *
	 * @param {string} entityClassName - entityClassName.
	 * @param {string} title - Display name.
	 * @param {array} excludedIDs - a list of pre-selected items in selection list.
	 * @param {string} filter - filter.
	 * @param {string} columnDefinition -columnDefinition.
	 * @param {boolean} multiSelection - if true then this Dialog will allow to select several items.
	 * @param {boolean} viewMode - if true then this Dialog just show list of related Objects and selection will be disabled.
	 * @param {string} allowedTypes - list of objects for selection will be filtered by these Allowed Types.
	 * @param {string} parentView - if set and parent view will be closed then this Dialog will be closed automatically.
	 * @param {boolean} allowResetFilter - enable checkbox "Reset filter" to enable search without default filter
	 * @param {string} resetFilterExpression - filter expressions which is used when default filter is reset
	 * @param {boolean} onlyTypedSearch - if set then grid is visible only when some EntityType is selected
	 * @param {object} presetFields - object with predefined data which is used for "Create" action in selection grid
	 *
	 * @returns {promise} - promise, which will be resolved with items that user selected on this Dialog.
	 *
	 */
	function selectObjects(
		entityClassName,
		title,
		excludedIDs,
		filter,
		columnDefinition,
		multiSelection,
		viewMode,
		allowedTypes,
		parentView,
		allowResetFilter,
		resetFilterExpression,
		onlyTypedSearch,
		presetFields
	) {
		return multiSelectionTabService.open(
			{
				title: title,
				viewMode: viewMode,
				multiSelection: !viewMode && multiSelection,
				entityClassName: entityClassName,
				allowedTypes: allowedTypes,
				filter: filter,
				correctEntityFilter: filter,
				excludedIDs: excludedIDs,
				columnDefinition: columnDefinition,
				onlyTypedSelectSearch: onlyTypedSearch,
				allowResetFilter: allowResetFilter,
				resetFilterExpression: resetFilterExpression,
				presetFields: presetFields,
			},
			parentView
		);
	}

	/**
	 * @ngdoc method
	 * @name mx.SolutionBuilderAgent.DialogOpenerService#openObjectsListPreview
	 *
	 * @description
	 * Opens a Dialog with list of objects with given `entityClassName` and `ids`, or by `filterExpression`
	 */
	function openObjectsListPreview(options, parentView) {
		return multiSelectionTabService.open(
			{
				title: options.title || options.label,
				viewMode: true,
				multiSelection: options.multiSelection,
				showActionsPanel: options.showActionsPanel,
				showSelectAll: options.showSelectAll,
				entityClassName: options.entityClassName,
				relation: options.relation,
				archived: options.archived,
				allowedTypes: options.allowedTypes,
				filter: options.filterExpression,
				correctEntityFilter: options.filterExpression,
				excludedIDs: options.excludedIDs,
				objectIDs: options.selectedItemIds,
				columnDefinition: options.columnDefinition,
				useObjectId: options.useObjectId,
			},
			parentView
		);
	}

	/**
	 * @ngdoc method
	 * @name mx.SolutionBuilderAgent.DialogOpenerService#selectHierarchyObject
	 *
	 * @description
	 * Opens a `Select Hierarchical Objects` dialog with tree-view.
	 *
	 * @param {string} entityClassName - entityClassName.
	 * @param {string} relationDisplayName - Display name.
	 * @param {DetailsView} parentView - if set and parent view will be closed then this Dialog will be closed automatically.
	 * @param {string} parentExpression - parent expression.
	 * @param {string} selectedId - pre-selected node id.
	 * @param {string} filter - filter expression.
	 * @param {string} rootElement - root element for tree data
	 * @param {boolean} includeRoot - include root element in result set if root element is specified
	 * @param {string} columnToDisplay column to use as a display value
	 * @param {string} dataQueryName specific Data Query name
	 * @returns {promise} - promise, which will be resolved with selected item that user selected on this Dialog.
	 */
	function selectHierarchyObject(
		entityClassName,
		relationDisplayName,
		parentView,
		parentExpression,
		selectedId,
		filter,
		rootElement,
		includeRoot,
		columnToDisplay,
		dataQueryName
	) {
		return _openSelectView(
			'selectHierarchyObjects_' + relationDisplayName + Math.random(),
			relationDisplayName || i18n.get('solutionBuilderAgent.browseDialog.select_label'),
			i18n.get('solutionBuilderAgent.browseDialog.select_objects_short'),
			relationDisplayName,
			{
				entityClassName: entityClassName,
				filter: filter,
				parentExpression: parentExpression,
				displayExpression: columnToDisplay,
				selectedId: selectedId || null,
				rootElement: rootElement,
				includeRoot: includeRoot,
				dataQueryName: dataQueryName,
			},
			'mx.SolutionBuilderAgent.ObjectPickerHierarchyViewController',
			'workspaces/SolutionBuilderAgent/layouts/hierarchy-view/hierarchy-view.html',
			parentView
		);
	}

	/**
	 * @ngdoc method
	 * @name mx.SolutionBuilderAgent.DialogOpenerService#selectFiles
	 *
	 * @description
	 * Opens a `Select Files` dialog.
	 *
	 * @param {Object} options - image options.
	 * @param {string} relatedObjectId - relatedObjectId.
	 * @param {string} relatedEntity - relatedEntity.
	 * @param {DetailsView} parentView - if set and parent view will be closed then this Dialog will be closed automatically.
	 *
	 * @returns {promise} - promise, which will be resolved with files that user selected on this Dialog.
	 *
	 */
	function selectImage(options, relatedObjectId, relatedEntity, parentView) {
		const locals = {
			modeAttach: options.modeAttach,
			modeIcon: options.modeIcon,
			modeImage: options.modeImage,
			serverBasePath: options.serverBasePath,
			relatedEntity: relatedEntity,
			relatedObjectId: relatedObjectId,
			baseFolder: options.baseFolder,
			selectedImage: options.selectedImage,
		};

		return _openSelectView(
			'selectFiles_' + relatedEntity + relatedObjectId + Math.random(),
			i18n.get('solutionBuilderAgent.browseDialog.selectImageTitle'),
			i18n.get('solutionBuilderAgent.browseDialog.selectImage'),
			undefined,
			locals,
			'mx.SolutionBuilderAgent.ImageSelectionViewController',
			'workspaces/SolutionBuilderAgent/layouts/image-selection-view/image-selection-view.html',
			parentView
		);
	}

	/**
	 * @ngdoc method
	 * @name mx.SolutionBuilderAgent.DialogOpenerService#selectFiles
	 *
	 * @description
	 * Opens a `Select Files` dialog.
	 *
	 * @param {string} relatedObjectId - relatedObjectId.
	 * @param {string} relatedEntity - relatedEntity.
	 * @param {boolean} skipInitialLoad - skip initial load.
	 * @param {DetailsView} parentView - if set and parent view will be closed then this Dialog will be closed automatically.
	 *
	 * @returns {promise} - promise, which will be resolved with files that user selected on this Dialog.
	 *
	 */
	function selectFiles(relatedObjectId, relatedEntity, skipInitialLoad, parentView) {
		const locals = {
			relatedObjectId: relatedObjectId,
			relatedEntity: relatedEntity,
			skipInitialLoad: skipInitialLoad === true,
			selectionMode: mx.SolutionBuilderAgent.DetailsViewSelectionMode.Single,
		};

		return _openSelectView(
			'selectFiles_' + relatedEntity + relatedObjectId + Math.random(),
			i18n.get('solutionBuilderAgent.browseDialog.selectFilesTitle'),
			i18n.get('solutionBuilderAgent.browseDialog.selectFiles'),
			undefined,
			locals,
			'mx.SolutionBuilderAgent.ObjectPickerFileSelectionTabController',
			'workspaces/SolutionBuilderAgent/components/mx-grid/file-selection-tab.html',
			parentView
		);
	}

	function _openSelectView(uniqueViewId, name, headerTitle, displayName, locals, controller, templateUrl, parentView, keepAsOpened) {
		const selView = new MxDetailsView({
			controller: controller,
			headerTitle: headerTitle,
			displayName: displayName,
			id: uniqueViewId,
			locals: locals,
			name: name,
			keepAsOpened: keepAsOpened,
			templateUrl: templateUrl,
		});

		locals.__view = selView;

		return sidePanelService.addView(selView, parentView, true);
	}
}
